'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/auth-context';
import { Skeleton } from '@/components/ui/skeleton';
import { WritingTask, getLatestWritingTask } from '@/lib/firebase/writing';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { PlusCircle, FolderOpen, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { TaskList } from './task-list';
import { WritingTaskType, BodyParagraphData } from '@/types/writing';

interface BaseWritingPageProps {
  taskType: WritingTaskType;
  title: string;
  newButtonText: string;
  loadButtonText: string;
  sheetTitle: string;
  authRequiredText: string;
  children: (props: {
    selectedTask: WritingTask | null;
    triggerSuggestionClear: number;
    defaultBodyParagraphs: BodyParagraphData[];
  }) => React.ReactNode;
  onTaskSelect?: (task: WritingTask) => void;
}

export function BaseWritingPage({
  taskType,
  title,
  newButtonText,
  loadButtonText,
  sheetTitle,
  authRequiredText,
  children,
  onTaskSelect,
}: BaseWritingPageProps) {
  const { user, loading: authLoading } = useAuth();
  const [selectedTask, setSelectedTask] = useState<WritingTask | null>(null);
  const [isLoadSheetOpen, setIsLoadSheetOpen] = useState(false);
  const [triggerSuggestionClear, setTriggerSuggestionClear] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const defaultBodyParagraphs = useMemo(
    () => [
      { type: '' as const, content: '' },
      { type: '' as const, content: '' },
    ],
    []
  );

  useEffect(() => {
    if (!authLoading && user) {
      const fetchLatest = async () => {
        setIsInitialLoading(true);
        try {
          const latestTask = await getLatestWritingTask(user.uid, taskType);
          if (latestTask) {
            setSelectedTask(latestTask);
          }
        } catch (error) {
          console.error('Failed to fetch latest task:', error);
        } finally {
          setIsInitialLoading(false);
        }
      };
      fetchLatest();
    } else if (!authLoading && !user) {
      setIsInitialLoading(false);
    }
  }, [user, authLoading, taskType]);

  const handleSelectTask = useCallback(
    async (task: WritingTask) => {
      if (task.taskType === taskType) {
        setSelectedTask(task);
        setIsLoadSheetOpen(false);
        setTriggerSuggestionClear(0);
        onTaskSelect?.(task);
      } else {
        setSelectedTask(null);
        setIsLoadSheetOpen(false);
        setTriggerSuggestionClear(0);
      }
    },
    [taskType, onTaskSelect]
  );

  const handleNewTask = () => {
    setSelectedTask(null);
    setTriggerSuggestionClear(0);
    setIsInitialLoading(false);
  };

  if (authLoading || isInitialLoading) {
    return (
      <div className='container mx-auto p-4 flex flex-col flex-grow space-y-4'>
        <div className='flex justify-between items-center mb-4'>
          <Skeleton className='h-8 w-48' />
          <div className='flex gap-2'>
            <Skeleton className='h-9 w-24' />
            <Skeleton className='h-9 w-24' />
          </div>
        </div>
        <div className='space-y-4 border rounded-lg p-4 shadow-sm flex-grow'>
          <Skeleton className='h-8 w-1/4 mb-4' />
          <Skeleton className='h-10 w-full mb-2' />
          <Skeleton className='h-64 w-full mb-4' />
          <Skeleton className='h-9 w-full' />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className='container mx-auto p-4 flex justify-center items-center flex-grow'>
        <Card className='max-w-md w-full'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Info className='h-5 w-5' />
              Authentication Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className='text-muted-foreground'>{authRequiredText}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto p-0 sm:p-4 flex flex-col flex-grow space-y-4'>
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 flex-shrink-0'>
        <h1 className='text-2xl font-bold'>{title}</h1>
        <div className='flex gap-2 self-end sm:self-center'>
          <Button
            variant='outline'
            size='icon'
            className='sm:hidden'
            onClick={handleNewTask}
            aria-label={newButtonText}
            title={newButtonText}
          >
            <PlusCircle className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            size='sm'
            className='hidden sm:inline-flex items-center'
            onClick={handleNewTask}
          >
            <PlusCircle className='h-4 w-4' /> {newButtonText}
          </Button>

          <Sheet open={isLoadSheetOpen} onOpenChange={setIsLoadSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant='outline'
                size='icon'
                className='sm:hidden'
                aria-label={loadButtonText}
                title={loadButtonText}
              >
                <FolderOpen className='h-4 w-4' />
              </Button>
            </SheetTrigger>
            <SheetTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='hidden sm:inline-flex items-center'
              >
                <FolderOpen className='h-4 w-4' /> {loadButtonText}
              </Button>
            </SheetTrigger>
            <SheetContent
              className={cn(
                'w-full h-screen sm:h-full sm:max-w-lg',
                'flex flex-col'
              )}
            >
              <SheetHeader className='flex-shrink-0'>
                <SheetTitle>{sheetTitle}</SheetTitle>
              </SheetHeader>
              <div className='flex-grow overflow-hidden py-4'>
                <TaskList
                  onSelectTask={handleSelectTask}
                  selectedTaskId={selectedTask?.id || null}
                  filterTaskType={taskType}
                />
              </div>
              <SheetFooter className='flex-shrink-0'>
                <SheetClose asChild>
                  <Button variant='outline'>Cancel</Button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className='flex-grow'>
        {children({
          selectedTask,
          triggerSuggestionClear,
          defaultBodyParagraphs,
        })}
      </div>
    </div>
  );
}
